import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  Image,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Stack,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useState, useMemo, useCallback } from 'react';
import { useForm, useWatch, Controller, FormProvider } from 'react-hook-form';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { zodResolver } from '@hookform/resolvers/zod';
import { ImagePlus, X, Edit, CheckCircle2, Archive } from 'lucide-react';
import { Select, SingleValue } from 'chakra-react-select';
import { djs } from '@duke-hq/djs';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import {
  ProviderCauseVisibilitySchema,
  type ProviderCauseVisibility,
} from '@duke-hq/svc-api-db/schemas/enums/ProviderCauseVisibility.schema';
import { ProviderCauseStatusSchema } from '@duke-hq/svc-api-db/schemas/enums/ProviderCauseStatus.schema';
import { StatesSchema } from '@duke-hq/svc-api-db/schemas/enums/States.schema';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import {
  CauseFormSchema,
  CauseFormInput,
  CreateCauseSchema,
} from '@duke-hq/svc-api/provider/trpc/routers/tenant/groupRewards/schemas';
import { CauseFormVisibilityFields } from './CauseFormVisibilityFields';
import { FileUploadContextDirect } from '@duke-hq/svc-files-ui/FileUploadContextDirect';
import { FileUploadTrigger } from '@duke-hq/svc-files-ui/FileUploadTrigger';
import { ImageUploadDisplay } from '@duke-hq/svc-files-ui/ImageUploadDisplay';
import { ACCEPTABLE_IMAGES } from '@duke-hq/svc-files-lib/schemas';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';

type SelectOption = {
  label: string;
  value: string;
};

type CauseCardProps = {
  name: string;
  imageUrl: string | null;
  targetAmount: number;
  visibility: ProviderCauseVisibility;
};

const defaultFormState: CauseFormInput = {
  name: '',
  description: undefined,
  websiteUrl: 'https://',
  targetAmount: 5,
  timeframeDate: '',
  visibility: undefined,
  siteRegionIds: undefined,
  siteLocationIds: undefined,
  states: undefined,
  rationale: undefined,
  imageDrn: undefined,
};

function parseStates(states: string[] | undefined): (keyof typeof StatesSchema.enum)[] | undefined {
  if (!states || states.length === 0) {
    return undefined;
  }

  const parsedStates = states.filter(
    (state): state is keyof typeof StatesSchema.enum => state in StatesSchema.enum,
  );

  return parsedStates.length > 0 ? parsedStates : undefined;
}

function CauseCard({
  cause,
  locations,
}: {
  cause: CauseCardProps;
  locations: SelectOption[] | null;
}) {
  const { name, imageUrl, targetAmount, visibility } = cause;

  const locationLabel = useMemo(() => {
    const labelMap = {
      [ProviderCauseVisibilitySchema.enum.REGION]: {
        prefix: 'Regional',
        plural: 'regions',
      },
      [ProviderCauseVisibilitySchema.enum.SITE]: {
        prefix: 'Local',
        plural: 'sites',
      },
      [ProviderCauseVisibilitySchema.enum.STATE]: {
        prefix: 'State',
        plural: 'states',
      },
    };

    if (visibility === ProviderCauseVisibilitySchema.enum.NATIONAL) {
      return (
        <Text fontSize="sm" color="fg.muted">
          National
        </Text>
      );
    }

    const label = labelMap[visibility] ?? {
      prefix: 'Location',
      plural: 'locations',
    };

    if (locations != null && locations.length > 1) {
      return (
        <Flex fontSize="sm" color="fg.muted" align="center" gap={1}>
          <Text fontWeight="semibold">{label.prefix}:</Text>

          {visibility === ProviderCauseVisibilitySchema.enum.STATE ? (
            locations.map((loc) => loc.value).join(',')
          ) : (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              borderRadius="5px"
              borderColor="brand.500"
              bg="brand.500"
              color="white"
              borderWidth="1px"
              px={2}
              fontSize="xs"
              fontWeight="bold"
              sx={{ textTransform: 'none' }}
            >
              {locations.length} {label.plural}
            </Box>
          )}
        </Flex>
      );
    }

    return (
      <Text fontSize="sm" color="fg.muted">
        <Text as="span" fontWeight="semibold">
          {label.prefix}:
        </Text>{' '}
        {locations?.[0]?.label ?? 'N/A'}
      </Text>
    );
  }, [visibility, locations]);

  return (
    <Box
      bg="bg.muted"
      borderRadius="lg"
      boxShadow="sm"
      p={2}
      maxW="md"
      w="full"
      display="flex"
      alignItems="center"
      gap={4}
    >
      {imageUrl == null ? (
        <Flex
          w="150px"
          h="100px"
          borderRadius="md"
          bg="bg.subtle"
          align="center"
          justify="center"
          flexShrink={0}
        >
          <Icon as={ImagePlus} boxSize={6} color="fg.muted" />
        </Flex>
      ) : (
        <Stack w="150px" h="100px">
          <Image
            src={typeof imageUrl === 'string' ? imageUrl : undefined}
            w="full"
            h="100px"
            objectFit="cover"
            borderRadius="md"
          />
        </Stack>
      )}
      <Flex direction="column" alignItems="flex-start" textAlign="left">
        <Text fontWeight="bold" fontSize="sm" mb={1}>
          {name}
        </Text>

        <Text fontSize="sm" color="fg.muted">
          <Text as="span" fontWeight="semibold">
            Goal:
          </Text>{' '}
          ${targetAmount}
        </Text>

        {locationLabel}
      </Flex>
    </Box>
  );
}

export function CauseForm({
  onClose,
  editCause,
  onApproveCause,
  onArchiveCause,
  approvingCauseId,
  archivingCauseId,
}: {
  onClose: () => void;
  editCause?: RouterOutputs['groupRewards']['listCausesWithInfiniteScroll']['items'][number];
  onApproveCause?: (causeId: string) => void;
  onArchiveCause?: (causeId: string) => void;
  approvingCauseId?: string;
  archivingCauseId?: string;
}): React.ReactElement {
  const trpcUtils = trpcTenant.useUtils();
  const [nameToValidate, setNameToValidate] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [selectedVisibilityLocations, setSelectedVisibilityLocations] = useState<
    SelectOption[] | null
  >(() => {
    if (!editCause) {
      return null;
    }

    // For region visibility, use siteRegionIds
    if (
      editCause.visibility === ProviderCauseVisibilitySchema.enum.REGION &&
      editCause.siteRegionIds
    ) {
      return editCause.siteRegionIds.map((id) => ({ label: id, value: id }));
    }

    // For site visibility, use siteLocationIds
    if (
      editCause.visibility === ProviderCauseVisibilitySchema.enum.SITE &&
      editCause.siteLocationIds
    ) {
      return editCause.siteLocationIds.map((id) => ({
        label: id,
        value: id,
      }));
    }

    // For state visibility, use states
    if (editCause.visibility === ProviderCauseVisibilitySchema.enum.STATE && editCause.states) {
      return editCause.states.map((state) => ({
        label: state,
        value: state,
      }));
    }

    return null;
  });
  const [newCause, setNewCause] = useState<CauseCardProps | null>(null);
  const [isEditingEnabled, setIsEditingEnabled] = useState<boolean>(!editCause);

  const toast = useToast();
  const utils = trpcTenant.useUtils();
  const isEditMode = !!editCause;

  const visibilityOptions = useMemo<SelectOption[]>(
    () => [
      { label: t`National`, value: ProviderCauseVisibilitySchema.enum.NATIONAL },
      { label: t`Region`, value: ProviderCauseVisibilitySchema.enum.REGION },
      { label: t`Site`, value: ProviderCauseVisibilitySchema.enum.SITE },
      { label: t`State`, value: ProviderCauseVisibilitySchema.enum.STATE },
    ],
    [],
  );

  const handleEnableEditing = useCallback(() => {
    setIsEditingEnabled(true);
  }, []);

  const handleApproveCause = useCallback(() => {
    if (editCause && onApproveCause) {
      onApproveCause(editCause.id);
      onClose();
    }
  }, [editCause, onApproveCause, onClose]);

  const handleArchiveCause = useCallback(() => {
    if (editCause && onArchiveCause) {
      onArchiveCause(editCause.id);
      onClose();
    }
  }, [editCause, onArchiveCause, onClose]);

  const isApproving = editCause ? approvingCauseId === editCause.id : false;
  const isArchiving = editCause ? archivingCauseId === editCause.id : false;

  const methods = useForm<CauseFormInput>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: zodResolver(CauseFormSchema),
    defaultValues: editCause
      ? {
          name: editCause.name,
          description: editCause.description ?? undefined,
          websiteUrl: editCause.websiteUrl ?? '',
          targetAmount: editCause.targetAmount,
          timeframeDate: editCause.timeframeDate
            ? djs(editCause.timeframeDate).format('YYYY-MM-DD')
            : '',
          visibility: editCause.visibility ?? undefined,
          siteRegionIds: editCause.siteRegionIds ?? undefined,
          siteLocationIds: editCause.siteLocationIds ?? undefined,
          states: parseStates(editCause.states),
          rationale: editCause.rationale ?? undefined,
          imageDrn: editCause.imageDrn ?? undefined,
        }
      : defaultFormState,
  });

  const {
    handleSubmit,
    register,
    clearErrors,
    setValue,
    setError,
    control,
    formState: { errors, isSubmitting },
  } = methods;

  const name = useWatch({
    control,
    name: 'name',
  });

  const visibility = useWatch({
    control,
    name: 'visibility',
  });

  const createCause = trpcTenant.groupRewards.createCause.useMutation({
    onSuccess: (data) => {
      setNewCause({
        name: data.name,
        imageUrl: data.imageUrl ?? null,
        targetAmount: data.targetAmount,
        visibility: data.visibility ?? ProviderCauseVisibilitySchema.enum.NATIONAL,
      });

      void trpcUtils.groupRewards.listCausesWithInfiniteScroll.invalidate();
      setIsSuccess(true);

      toast({
        title: t`Success`,
        description: t`Cause created successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) => {
      toast({
        title: error.message || t`Failed to create a provider cause.`,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
    onSettled() {
      void utils.groupRewards.listCauses.refetch();
    },
  });

  const updateCause = trpcTenant.groupRewards.updateCause.useMutation({
    onSuccess: () => {
      toast({
        title: t`Success`,
        description: t`Cause updated successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      void trpcUtils.groupRewards.listCausesWithInfiniteScroll.invalidate();
      onClose();
    },
    onError: (error) => {
      toast({
        title: error.message || t`Failed to update the provider cause.`,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
    onSettled() {
      void utils.groupRewards.listCauses.refetch();
    },
  });

  const { refetch: checkNameExistence } = trpcTenant.groupRewards.validateUniqueName.useQuery(
    { name },
    { enabled: Boolean(nameToValidate) },
  );

  const validateName = async () => {
    if (name == null) {
      return true;
    }

    setNameToValidate(true);
    const result = await checkNameExistence();
    setNameToValidate(false);

    return !(result.data ?? false);
  };

  const onSubmit = async (data: CauseFormInput) => {
    // Only validate name uniqueness for new causes, not when updating existing ones
    if (!isEditMode) {
      const isNameValid = await validateName();

      if (isNameValid === false) {
        setError('name', {
          type: 'manual',
          message: t`A cause with this name already exists. Please choose a different name.`,
        });

        setNameToValidate(false);
        return;
      }
    }

    // Transform form data to API payload using the schema
    const payload = CreateCauseSchema.parse({
      ...data,
    });

    if (isEditMode && editCause != null) {
      // Update existing cause
      await updateCause.mutateAsync({
        causeId: editCause.id,
        ...payload,
      });
    } else {
      // Create new cause
      await createCause.mutateAsync(payload);
    }
  };

  return (
    <FormProvider {...methods}>
      <Box as="form" onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }} noValidate>
        <VStack spacing={4} align="stretch" position="relative">
          {isSuccess === true && newCause != null ? (
            <VStack
              position="absolute"
              inset="0"
              zIndex="10"
              bg="rgba(255, 255, 255, 0.9)"
              backdropFilter="blur(5px)"
              align="center"
              justify="center"
              borderRadius="md"
              textAlign="center"
              spacing={4}
            >
              <CauseCard cause={newCause} locations={selectedVisibilityLocations} />
              <Text fontSize="2xl" fontWeight="bold" color="brand.500" mt={4}>
                <Trans>Suggestion Submitted!</Trans>
              </Text>
              <Text mt={2} color="brand.500" width="500px">
                <Trans>
                  Thanks for your suggestion! You'll be notified if your submission has been
                  accepted. If you have any further questions, please see the Good Effort program
                  FAQS.
                </Trans>
              </Text>
              <Button onClick={onClose} mt={6} variant="outline">
                <Trans>Done</Trans>
              </Button>
            </VStack>
          ) : null}

          <Stack justify="flex-start" align="start" px={6}>
            <Flex justify="space-between" align="center" w="full">
              <Text size="md" fontWeight="bold">
                {isEditMode ? <Trans>Edit cause</Trans> : <Trans>Suggest a cause</Trans>}
              </Text>
              {isEditMode != null && !isEditingEnabled && (
                <Button
                  size="xs"
                  leftIcon={<Icon as={Edit} />}
                  onClick={handleEnableEditing}
                  variant="outline"
                >
                  <Trans>Edit</Trans>
                </Button>
              )}
            </Flex>
          </Stack>

          <FormMainContainer>
            <FormContentContainer>
              <FormControl>
                <Controller
                  control={control}
                  name="imageDrn"
                  render={({ field }) => (
                    <FileUploadContextDirect
                      quantity={{ min: 1, max: 1 }}
                      accept={ACCEPTABLE_IMAGES}
                      attachedToKind="cause"
                      onFileComplete={(result) => field.onChange(result.drn)}
                    >
                      <VStack alignItems="center" spacing={2}>
                        {isEditMode && !isEditingEnabled ? (
                          <Stack
                            bg="bg.subtle"
                            p={4}
                            rounded="10px"
                            w="240px"
                            h="160px"
                            cursor="pointer"
                            userSelect="none"
                            position="relative"
                          >
                            <ImageUploadDisplay imageUrl={editCause.imageUrl} isDisabled />
                          </Stack>
                        ) : (
                          <FileUploadTrigger>
                            <Stack
                              bg="bg.subtle"
                              p={4}
                              rounded="10px"
                              w="240px"
                              h="160px"
                              cursor="pointer"
                              userSelect="none"
                              position="relative"
                            >
                              <ImageUploadDisplay imageUrl={editCause?.imageUrl} />
                              {editCause?.imageDrn != null ? (
                                <Button
                                  position="absolute"
                                  top="0"
                                  right="0"
                                  size="sm"
                                  variant="solid"
                                  bg="fg.subtle"
                                  color="bg.surface"
                                  _hover={{ bg: 'fg.muted' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    field.onChange(undefined);
                                  }}
                                  minW="auto"
                                  h="8"
                                  w="8"
                                  p="0"
                                  borderRadius="full"
                                  boxShadow="md"
                                  zIndex="2"
                                >
                                  <Icon as={X} boxSize={4} />
                                </Button>
                              ) : null}
                            </Stack>
                          </FileUploadTrigger>
                        )}
                        <Text fontSize="xs" color="fg.muted" textAlign="center" maxW="240px">
                          <Trans>
                            Image preview shows how it will appear in the 3:2 aspect ratio (e.g.,
                            600×400px)
                          </Trans>
                        </Text>
                      </VStack>
                    </FileUploadContextDirect>
                  )}
                />
              </FormControl>
              <FormControl isRequired isInvalid={!!errors.name}>
                <FormLabel htmlFor="CauseForm-name">
                  <Trans>Name of cause</Trans>
                </FormLabel>
                <Input
                  id="CauseForm-name"
                  autoComplete="off"
                  isDisabled={!!(isEditMode && !isEditingEnabled)}
                  bg={isEditMode && !isEditingEnabled ? 'bg.muted' : 'bg.surface'}
                  color={isEditMode && !isEditingEnabled ? 'fg.muted' : 'inherit'}
                  {...register('name')}
                />
                <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={!!errors.websiteUrl}>
                <FormLabel htmlFor="CauseForm-website">
                  <Trans>Website URL</Trans>
                </FormLabel>
                <Input
                  id="CauseForm-website"
                  autoComplete="off"
                  isDisabled={!!(isEditMode && !isEditingEnabled)}
                  bg={isEditMode && !isEditingEnabled ? 'bg.muted' : 'bg.surface'}
                  color={isEditMode && !isEditingEnabled ? 'fg.muted' : 'inherit'}
                  {...register('websiteUrl')}
                />
                <FormErrorMessage>{errors.websiteUrl?.message}</FormErrorMessage>
              </FormControl>

              <HStack w="full" align="center" gap={2}>
                <FormControl isRequired isInvalid={!!errors.targetAmount}>
                  <FormLabel htmlFor="CauseForm-targetAmount">
                    <Trans>Target amount</Trans>
                  </FormLabel>
                  <Controller
                    name="targetAmount"
                    control={control}
                    render={({ field }) => (
                      <NumberInput
                        min={5}
                        precision={2}
                        step={5}
                        isDisabled={!!(isEditMode && !isEditingEnabled)}
                        onChange={(_, valueAsNumber) => {
                          field.onChange(valueAsNumber);
                        }}
                        value={field.value ?? ''}
                      >
                        <NumberInputField
                          id="CauseForm-targetAmount"
                          bg={isEditMode && !isEditingEnabled ? 'bg.muted' : 'bg.surface'}
                          color={isEditMode && !isEditingEnabled ? 'fg.muted' : 'inherit'}
                        />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    )}
                  />
                  <FormErrorMessage>{errors.targetAmount?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.timeframeDate}>
                  <FormLabel htmlFor="CauseForm-timeframeDate">
                    <Trans>End date</Trans>
                  </FormLabel>
                  <Input
                    id="CauseForm-timeframeDate"
                    type="date"
                    min={djs().format('YYYY-MM-DD')}
                    isDisabled={!!(isEditMode && !isEditingEnabled)}
                    bg={isEditMode && !isEditingEnabled ? 'bg.muted' : 'bg.surface'}
                    color={isEditMode && !isEditingEnabled ? 'fg.muted' : 'inherit'}
                    {...register('timeframeDate')}
                  />
                  <FormErrorMessage>{errors.timeframeDate?.message}</FormErrorMessage>
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel htmlFor="CauseForm-description">
                  <Trans>Description</Trans>
                </FormLabel>
                <Textarea
                  id="CauseForm-description"
                  placeholder="Add a description"
                  size="sm"
                  isDisabled={!!(isEditMode && !isEditingEnabled)}
                  bg={isEditMode && !isEditingEnabled ? 'bg.muted' : 'bg.surface'}
                  color={isEditMode && !isEditingEnabled ? 'fg.muted' : 'inherit'}
                  {...register('description')}
                />
              </FormControl>

              <FormControl isRequired isInvalid={!!errors.visibility}>
                <FormLabel htmlFor="CauseForm-visibility">
                  <Trans>Visibility</Trans>
                </FormLabel>
                <Controller
                  name="visibility"
                  control={control}
                  render={({ field }) => (
                    <Select
                      id="visibility"
                      placeholder={t`Site, Region, State or National`}
                      options={visibilityOptions}
                      isDisabled={!!(isEditMode && !isEditingEnabled)}
                      styles={{
                        control: (provided: React.CSSProperties) => ({
                          ...provided,
                          backgroundColor: isEditMode && !isEditingEnabled ? '#f7fafc' : 'white',
                          color: isEditMode && !isEditingEnabled ? '#718096' : 'inherit',
                        }),
                      }}
                      value={
                        visibilityOptions?.find((option) => option.value === field.value) || null
                      }
                      onChange={(selectedVisibility: SingleValue<SelectOption> | null) => {
                        field.onChange(selectedVisibility?.value ?? undefined);

                        // Reset dependent fields when visibility changes
                        setValue('siteRegionIds', undefined);
                        setValue('siteLocationIds', undefined);
                        setValue('states', undefined);

                        clearErrors(['siteRegionIds', 'siteLocationIds', 'states']);
                      }}
                    />
                  )}
                />
                <FormErrorMessage>{errors.visibility?.message}</FormErrorMessage>
              </FormControl>

              {visibility != null && (
                <CauseFormVisibilityFields
                  visibility={visibility}
                  disabled={!!(isEditMode && !isEditingEnabled)}
                  onSelect={(selected: SelectOption[] | null) => {
                    if (selected == null) {
                      return;
                    }

                    setSelectedVisibilityLocations(selected);
                  }}
                />
              )}

              <FormControl>
                <FormLabel htmlFor="CauseForm-rationale">
                  <Trans>What is the main reason for suggesting this cause?</Trans>
                </FormLabel>
                <Textarea
                  id="CauseForm-rationale"
                  placeholder={t`Add any further details or rationale`}
                  size="sm"
                  isDisabled={!!(isEditMode && !isEditingEnabled)}
                  bg={isEditMode && !isEditingEnabled ? 'bg.muted' : 'bg.surface'}
                  color={isEditMode && !isEditingEnabled ? 'fg.muted' : 'inherit'}
                  {...register('rationale')}
                />
              </FormControl>
            </FormContentContainer>
          </FormMainContainer>
          <FormFooterContainer>
            <HStack justify="flex-end" align="center" gap={2}>
              <Button variant="ghost" onClick={onClose}>
                <Trans>Close</Trans>
              </Button>

              {isEditMode &&
              !isEditingEnabled &&
              onApproveCause &&
              onArchiveCause &&
              editCause != null ? (
                <>
                  <Button
                    leftIcon={<Icon as={CheckCircle2} />}
                    size="xs"
                    isDisabled={
                      editCause?.status != null
                        ? editCause?.status === ProviderCauseStatusSchema.enum.APPROVED
                        : false || isApproving || isArchiving
                    }
                    isLoading={isApproving}
                    loadingText="Approving..."
                    onClick={handleApproveCause}
                  >
                    <Trans>Approve</Trans>
                  </Button>
                  <Button
                    leftIcon={<Icon as={Archive} />}
                    variant="outline"
                    size="xs"
                    colorScheme="red"
                    isDisabled={
                      editCause?.status != null
                        ? editCause?.status === ProviderCauseStatusSchema.enum.ARCHIVED
                        : false || isApproving || isArchiving
                    }
                    isLoading={isArchiving}
                    loadingText="Archiving..."
                    onClick={handleArchiveCause}
                  >
                    <Trans>Archive</Trans>
                  </Button>
                </>
              ) : null}

              {isEditMode && isEditingEnabled ? (
                <Button type="submit" isLoading={isSubmitting || updateCause.isPending}>
                  <Trans>Update</Trans>
                </Button>
              ) : null}

              {!isEditMode ? (
                <Button type="submit" isLoading={isSubmitting || createCause.isPending}>
                  <Trans>Suggest</Trans>
                </Button>
              ) : null}
            </HStack>
          </FormFooterContainer>
        </VStack>
      </Box>
    </FormProvider>
  );
}
